import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientLink } from '../components/EntityLinks';
import { Search, UserCircle, ChevronRight, ChevronLeft, Loader2, Plus, X, Trash2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import FilterableTh from '../components/FilterableTh';
import apiClient from '../../../api/api';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import useAutoRefresh from '../../../hooks/useAutoRefresh';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import PhoneInput from '../../../components/common/PhoneInput';
import PhoneNumbersField from '../../../components/common/PhoneNumbersField';
import CitySelect from '../../../components/common/CitySelect';
import { formatMobileNumber } from '../../../utils/phoneFormat';
import { HONORIFICS } from '../../../constants/honorifics';

const PAGE_SIZE = 50;

const REG_TABS = ['All', 'Pending', 'Invoiced', 'Receipt Submitted', 'Paid', 'Waived', 'Expired'];

const STATUS_CONFIG = {
  PENDING:          { dot: 'bg-amber-400',   text: 'text-amber-700',   bg: '',  label: 'Fee Pending' },
  INVOICED:         { dot: 'bg-blue-400',    text: 'text-blue-700',    bg: '',  label: 'Invoiced' },
  RECEIPT_UPLOADED: { dot: 'bg-violet-400',  text: 'text-violet-700',  bg: '',  label: 'Receipt Submitted' },
  PAID:             { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: '',  label: 'Paid' },
  WAIVED:           { dot: 'bg-slate-400',   text: 'text-slate-600',   bg: '',  label: 'Waived' },
  EXPIRED:          { dot: 'bg-rose-400',    text: 'text-rose-700',    bg: '',  label: 'Expired' },
};

const TAB_TO_STATUS = {
  Pending:            'PENDING',
  Invoiced:           'INVOICED',
  'Receipt Submitted':'RECEIPT_UPLOADED',
  Paid:               'PAID',
  Waived:             'WAIVED',
  Expired:            'EXPIRED',
};

const CLIENT_TYPES = ['INDIVIDUAL', 'CORPORATE_PROXY'];

const BLANK_FORM = {
  full_name: '', email: '', mobile_number: '', secondary_phone_numbers: [],
  gender: '', primary_address: '', city_id: '', client_type: 'INDIVIDUAL',
  honorific: '', company_name: '', display_name_source: 'FULL_NAME',
};

const SectionHeader = ({ title }) => (
  <div className="px-5 pt-5 pb-2.5 border-b border-slate-100">
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{title}</p>
  </div>
);

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const inputCls = (hasError) =>
  `w-full px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:border-blue-500 transition-colors ${
    hasError
      ? 'border-red-400 bg-red-50 focus:ring-red-100'
      : 'border-slate-300 bg-white text-slate-800 placeholder-slate-400 focus:ring-blue-100'
  }`;

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const ClientManagement = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [pendingMigrationOnly, setPendingMigrationOnly] = useState(false);
  const [contactPendingOnly, setContactPendingOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [counts, setCounts] = useState({ All: 0 });

  const [showDrawer, setShowDrawer] = useState(false);
  const [formData, setFormData] = useState(BLANK_FORM);
  const genderLockedByHonorific = formData.honorific === 'Mr.' || formData.honorific === 'Mrs.' || formData.honorific === 'Ms.';
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [credentialsSent, setCredentialsSent] = useState(false);
  const [createdClientId, setCreatedClientId] = useState(null);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  // SUPER_ADMIN override of the "has bookings/invoices" block
  const { isSuperAdmin } = useAdminAuth();
  const [blockedClients, setBlockedClients] = useState([]);
  const [overrideImpact, setOverrideImpact] = useState(null);
  const [overridePassword, setOverridePassword] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);

  // Reset back to page 1 whenever a filter/search changes underneath the current page.
  useEffect(() => { setPage(1); }, [activeTab, debouncedSearch, pendingMigrationOnly, contactPendingOnly]);

  useEffect(() => { fetchClients(); }, [activeTab, debouncedSearch, pendingMigrationOnly, contactPendingOnly, page]);

  // Clear selection whenever the underlying list changes to avoid acting on stale rows.
  useEffect(() => { setSelectedIds(new Set()); }, [activeTab, debouncedSearch, pendingMigrationOnly, contactPendingOnly, page]);

  const buildFilters = () => ({
    page,
    limit: PAGE_SIZE,
    ...(activeTab !== 'All' ? { status: TAB_TO_STATUS[activeTab] } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(pendingMigrationOnly ? { pending_migration: 'true' } : {}),
    ...(contactPendingOnly ? { contact_pending: 'true' } : {}),
  });

  const fetchClients = async ({ silent = false } = {}) => {
    // Only take over the whole page with the spinner on the very first load.
    // Later fetches (search/tab/page changes, auto-refresh) update in place so
    // the search input never unmounts and loses focus.
    const showSpinner = !silent && !hasLoadedOnce;
    try {
      if (showSpinner) setLoading(true);
      if (!silent) setError(null);
      const response = await apiClient.getAllClients(buildFilters());
      setClients(response.data || []);
      setPagination(response.pagination || null);
      if (response.counts) setCounts(response.counts);
      setHasLoadedOnce(true);
    } catch (err) {
      if (!silent) setError('Failed to load clients');
      else console.error('ClientManagement silent refresh error:', err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  // Pause while the "Add Client" drawer is open so a background refresh
  // can't disturb the form the admin is filling in.
  useAutoRefresh(() => fetchClients({ silent: true }), {
    intervalMs: 5000,
    enabled: !showDrawer,
  });

  const openAdd = () => {
    setFormData(BLANK_FORM);
    setFormError(null);
    setFormSuccess(null);
    setCredentialsSent(false);
    setCreatedClientId(null);
    setShowDrawer(true);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setFormError(null);
    setFormSuccess(null);
    setCredentialsSent(false);
    setCreatedClientId(null);
  };

  const toggleSelectMode = () => {
    setSelectMode(v => !v);
    setSelectedIds(new Set());
  };

  const toggleSelect = (clientId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev =>
      prev.size === clients.length ? new Set() : new Set(clients.map(c => c.client_profile_id))
    );
  };

  const openDeleteConfirm = () => {
    setDeleteConfirmText('');
    setDeleteError(null);
    resetOverride();
    setShowDeleteConfirm(true);
  };

  const resetOverride = () => {
    setBlockedClients([]);
    setOverrideImpact(null);
    setOverridePassword('');
  };

  const closeDeleteConfirm = () => {
    if (deleteLoading || overrideLoading) return;
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
    setDeleteError(null);
    resetOverride();
  };

  // SUPER_ADMIN only: ask the server what deleting the blocked clients would take with it.
  const handleStartOverride = async () => {
    setOverrideLoading(true);
    setDeleteError(null);
    try {
      const impacts = await Promise.all(blockedClients.map(c => apiClient.getClientDeleteImpact(c.id)));
      const deleted = {};
      impacts.forEach(r => {
        Object.entries(r.data?.deleted || {}).forEach(([table, n]) => {
          deleted[table] = (deleted[table] || 0) + n;
        });
      });
      setOverrideImpact(deleted);
    } catch (err) {
      setDeleteError(err.message || 'Could not work out what is attached to these clients.');
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleOverrideDelete = async () => {
    if (!overridePassword) return;
    setOverrideLoading(true);
    setDeleteError(null);
    try {
      const results = await Promise.allSettled(
        blockedClients.map(c => apiClient.forceDeleteClientProfile(c.id, overridePassword))
      );
      const failedIdx = results.map((r, i) => (r.status === 'rejected' ? i : -1)).filter(i => i >= 0);
      await fetchClients();
      if (failedIdx.length === 0) {
        setShowDeleteConfirm(false);
        setDeleteConfirmText('');
        resetOverride();
      } else {
        const wrongPassword = failedIdx.some(i => results[i].reason?.status === 401);
        setDeleteError(
          wrongPassword
            ? 'Incorrect password.'
            : (results[failedIdx[0]].reason?.message || 'Failed to delete the selected client(s).')
        );
        // Keep only the ones that are still there so a retry doesn't re-target deleted clients.
        if (!wrongPassword) setBlockedClients(prev => failedIdx.map(i => prev[i]));
      }
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (deleteConfirmText.trim().toLowerCase() !== 'confirm') return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const ids = Array.from(selectedIds);
      const results = await Promise.allSettled(ids.map(id => apiClient.deleteClientProfile(id)));
      const failed = results.filter(r => r.status === 'rejected');
      if (isSuperAdmin) {
        setBlockedClients(
          results
            .map((r, i) => ({ r, id: ids[i] }))
            .filter(({ r }) => r.status === 'rejected' && r.reason?.status === 409)
            .map(({ id }) => {
              const c = clients.find(x => x.client_profile_id === id);
              return { id, name: c ? `${c.honorific ? `${c.honorific} ` : ''}${c.full_name ?? 'Client'}` : 'Client' };
            })
        );
      }
      if (failed.length > 0) {
        setDeleteError(
          failed.length === ids.length
            ? (failed[0].reason?.message || 'Failed to delete selected client(s).')
            : `${failed.length} of ${ids.length} client(s) could not be deleted. They may have existing bookings or invoices.`
        );
      }
      setSelectedIds(new Set());
      await fetchClients();
      if (failed.length === 0) {
        setShowDeleteConfirm(false);
        setDeleteConfirmText('');
      }
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete selected client(s).');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    const genderFromHonorific = name === 'honorific'
      ? (value === 'Mr.' ? 'MALE' : (value === 'Mrs.' || value === 'Ms.') ? 'FEMALE' : null)
      : null;
    setFormData(p => ({
      ...p,
      [name]: value,
      ...(genderFromHonorific ? { gender: genderFromHonorific } : {}),
      ...(name === 'client_type' && value !== 'CORPORATE_PROXY' ? { company_name: '', display_name_source: 'FULL_NAME' } : {}),
      ...(name === 'company_name' && !value ? { display_name_source: 'FULL_NAME' } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCorporate = formData.client_type === 'CORPORATE_PROXY';
    if (isCorporate) {
      if (!formData.company_name) {
        setFormError('Company name is required for corporate clients.');
        return;
      }
    } else if (!formData.full_name || !formData.mobile_number || !formData.gender) {
      setFormError('Please fill in all required fields.');
      return;
    }
    setFormLoading(true);
    setFormError(null);
    setFormSuccess(null);
    try {
      const payload = {
        full_name: formData.full_name || undefined,
        email: formData.email || undefined,
        mobile_number: formData.mobile_number || undefined,
        secondary_phone_numbers: formData.secondary_phone_numbers.filter(Boolean),
        gender: formData.gender || undefined,
        primary_address: formData.primary_address || undefined,
        city_id: formData.city_id || undefined,
        client_type: formData.client_type || 'INDIVIDUAL',
        honorific: formData.honorific || undefined,
        company_name: formData.company_name || undefined,
        display_name_source: formData.client_type === 'CORPORATE_PROXY' && formData.company_name
          ? formData.display_name_source
          : 'FULL_NAME',
      };
      const res = await apiClient.createClientProfile(payload);
      setCredentialsSent(!!res.data?.tempPassword);
      setCreatedClientId(res.data?.clientProfileId || null);
      setFormSuccess(
        res.data?.tempPassword
          ? `Temporary password: ${res.data.tempPassword}`
          : (res.message || 'Company client profile created. Add a mobile number later via Edit Profile to enable login.')
      );
      fetchClients();
    } catch (err) {
      setFormError(err?.message || 'Failed to create client.');
    } finally {
      setFormLoading(false);
    }
  };

  const pendingMigrationCount = counts.pending_migration || 0;
  const contactPendingCount = counts.contact_pending || 0;
  const totalCount = pagination?.total_count ?? clients.length;
  const totalPages = pagination?.total_pages ?? 1;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * PAGE_SIZE, totalCount);

  if (loading) {
    return (
      <AdminLayout title="Client Management" subtitle="Loading…">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Client Management">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Client Management"
      subtitle="View and manage all registered clients."
      actions={
        <div className="flex items-center gap-2">
          {selectMode && selectedIds.size > 0 && (
            <button
              onClick={openDeleteConfirm}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-500 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected ({selectedIds.size})
            </button>
          )}
          <button
            onClick={toggleSelectMode}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              selectMode
                ? 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {selectMode ? 'Cancel' : 'Select'}
          </button>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-500 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Client
          </button>
        </div>
      }
    >
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1 w-fit flex-wrap">
          {REG_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                activeTab === tab
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab}
              <span className="ml-1.5 tabular-nums text-slate-400">{counts[tab] ?? 0}</span>
            </button>
          ))}
        </div>

        {pendingMigrationCount > 0 && (
          <button
            onClick={() => setPendingMigrationOnly(v => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors whitespace-nowrap ${
              pendingMigrationOnly
                ? 'bg-amber-100 border-amber-300 text-amber-800'
                : 'bg-white border-slate-200 text-slate-500 hover:text-slate-700'
            }`}
          >
            Pending Migration <span className="ml-1 tabular-nums">{pendingMigrationCount}</span>
          </button>
        )}

        {contactPendingCount > 0 && (
          <button
            onClick={() => setContactPendingOnly(v => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors whitespace-nowrap ${
              contactPendingOnly
                ? 'bg-amber-100 border-amber-300 text-amber-800'
                : 'bg-white border-slate-200 text-slate-500 hover:text-slate-700'
            }`}
          >
            Contact Info Pending <span className="ml-1 tabular-nums">{contactPendingCount}</span>
          </button>
        )}

        <div className="relative sm:ml-auto">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, phone, email…"
            className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none w-64"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {selectMode && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={clients.length > 0 && selectedIds.size === clients.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </th>
                )}
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Location</th>
                <FilterableTh
                  label="Reg. Status"
                  className="text-left px-4 py-3 text-xs"
                  value={activeTab}
                  onChange={setActiveTab}
                  options={REG_TABS.map(tab => ({ key: tab, label: tab, count: counts[tab] ?? 0 }))}
                />
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {clients.length === 0 ? (
                <tr>
                  <td colSpan={selectMode ? 6 : 5} className="text-center py-16 text-slate-400 text-sm">
                    No clients match your filters.
                  </td>
                </tr>
              ) : clients.map(client => (
                <tr
                  key={client.client_profile_id}
                  onClick={() => selectMode
                    ? toggleSelect(client.client_profile_id)
                    : navigate(`/admin/users/${client.client_profile_id}/detail`)}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  {/* Select */}
                  {selectMode && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(client.client_profile_id)}
                        onChange={() => toggleSelect(client.client_profile_id)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                  )}

                  {/* Client */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5 min-w-[180px]">
                      {client.profile_picture_url ? (
                        <img src={client.profile_picture_url} alt={client.full_name}
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-200" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 ring-1 ring-slate-200">
                          <UserCircle className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-slate-900 leading-tight">
                          <ClientLink id={client.client_profile_id}>{client.honorific ? `${client.honorific} ` : ''}{client.full_name ?? '—'}</ClientLink>
                        </p>
                        {client.client_code && (
                          <p className="text-xs text-slate-400 font-mono">{client.client_code}</p>
                        )}
                        {client.onboarding_status === 'PENDING_MIGRATION' && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                            Pending Migration
                          </span>
                        )}
                        {client.onboarding_status === 'CONTACT_PENDING' && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                            Contact Info Pending
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p className="text-slate-700">{formatMobileNumber(client.mobile_number) ?? '—'}</p>
                    <p className="text-xs text-slate-400">{client.email ?? '—'}</p>
                  </td>

                  {/* Location */}
                  <td className="px-4 py-3">
                    <span className="text-slate-600 text-sm">{client.primary_address ?? '—'}</span>
                  </td>

                  {/* Reg Status */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge status={client.reg_fee_status || 'PENDING'} />
                  </td>

                  {/* Arrow */}
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-slate-300 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {clients.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-xs text-slate-400">
            <span>
              Showing {rangeStart}-{rangeEnd} of {totalCount} client{totalCount !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!pagination?.has_prev}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <span className="px-2 tabular-nums">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={!pagination?.has_next}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Client Drawer */}
      {showDrawer && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={!formSuccess ? closeDrawer : undefined} />

          <div className="w-full max-w-xl bg-white flex flex-col shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
              <h2 className="text-sm font-semibold text-slate-900">Add New Client</h2>
              <button onClick={closeDrawer} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              {/* Personal */}
              <SectionHeader title="Personal Information" />
              <div className="px-5 pt-4 pb-2 space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Honorific">
                    <select name="honorific" value={formData.honorific} onChange={handleInput} className={inputCls(false)}>
                      <option value="">—</option>
                      {HONORIFICS.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </Field>
                  <div className="col-span-2">
                    <Field label="Full Name" required={formData.client_type !== 'CORPORATE_PROXY'}>
                      <input type="text" name="full_name" value={formData.full_name} onChange={handleInput}
                        placeholder="e.g. Saman Perera" className={inputCls(false)} />
                    </Field>
                  </div>
                </div>
                <Field label={`Gender${genderLockedByHonorific ? ' (locked by Title)' : ''}`} required={formData.client_type !== 'CORPORATE_PROXY'}>
                  <select name="gender" value={formData.gender} onChange={handleInput} disabled={genderLockedByHonorific}
                    className={`${inputCls(false)} ${genderLockedByHonorific ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}>
                    <option value="">Select gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </Field>
                <Field label="Client Type">
                  <select name="client_type" value={formData.client_type} onChange={handleInput} className={inputCls(false)}>
                    {CLIENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</option>)}
                  </select>
                </Field>
                {formData.client_type === 'CORPORATE_PROXY' && (
                  <>
                    <p className="text-xs text-slate-400 -mt-1">
                      Contact person details can be added later once known — only the company name is required now.
                    </p>
                    <Field label="Company Name" required>
                      <input type="text" name="company_name" value={formData.company_name} onChange={handleInput}
                        placeholder="e.g. ABC Holdings (Pvt) Ltd" className={inputCls(false)} />
                    </Field>
                    {formData.company_name && (
                      <Field label="Display Name">
                        <p className="text-xs text-slate-400 mb-1.5">
                          Name printed on this client's receipts, invoices, statements and quotations. Switchable anytime from the client's detail page.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setFormData(p => ({ ...p, display_name_source: 'FULL_NAME' }))}
                            className={`px-3 py-2 rounded-lg border text-left text-xs font-medium transition-all ${
                              formData.display_name_source === 'FULL_NAME'
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                            }`}
                          >
                            <p className="font-semibold">Client's Name</p>
                            <p className="truncate text-slate-400">{formData.full_name || 'Personal name'}</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData(p => ({ ...p, display_name_source: 'COMPANY_NAME' }))}
                            className={`px-3 py-2 rounded-lg border text-left text-xs font-medium transition-all ${
                              formData.display_name_source === 'COMPANY_NAME'
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                            }`}
                          >
                            <p className="font-semibold">Company Name</p>
                            <p className="truncate text-slate-400">{formData.company_name}</p>
                          </button>
                        </div>
                      </Field>
                    )}
                  </>
                )}
              </div>

              {/* Contact */}
              <SectionHeader title="Contact" />
              <div className="px-5 pt-4 pb-2 space-y-3">
                <Field label="Email">
                  <input type="email" name="email" value={formData.email} onChange={handleInput}
                    placeholder="e.g. saman@example.com" className={inputCls(false)} />
                </Field>
                <Field label="Mobile Number" required={formData.client_type !== 'CORPORATE_PROXY'}>
                  <PhoneInput name="mobile_number" value={formData.mobile_number} onChange={handleInput}
                    placeholder="e.g. 0771234567" />
                </Field>
                <Field label="Secondary Phone Numbers">
                  <PhoneNumbersField
                    value={formData.secondary_phone_numbers}
                    onChange={(nums) => setFormData(p => ({ ...p, secondary_phone_numbers: nums }))}
                    primaryNumber={formData.mobile_number}
                  />
                </Field>
              </div>

              {/* Address */}
              <SectionHeader title="Address" />
              <div className="px-5 pt-4 pb-6 space-y-3">
                <Field label="City">
                  <CitySelect
                    value={formData.city_id}
                    onChange={city => setFormData(p => ({ ...p, city_id: city ? city.city_id : '' }))}
                    className={inputCls(false)}
                  />
                </Field>
                <Field label="Primary Address">
                  <input type="text" name="primary_address" value={formData.primary_address} onChange={handleInput}
                    placeholder="e.g. 45/A, Galle Road, Dehiwala, Colombo" className={inputCls(false)} />
                </Field>
              </div>

              {formSuccess && (
                <div className="mx-5 mb-6 px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-lg">
                  <p className="font-semibold mb-0.5">Client created successfully.</p>
                  <p>{formSuccess}</p>
                  {credentialsSent && (
                    <p className="mt-1 text-emerald-600">
                      Login credentials (mobile number + temporary password) have been sent to the client via SMS,
                      and a welcome message via WhatsApp. They'll be required to set their own password on first login.
                    </p>
                  )}
                </div>
              )}

              {formError && (
                <div className="mx-5 mb-6 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">
                  {formError}
                </div>
              )}
            </form>

            {/* Footer */}
            <div className="flex items-center gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
              <button type="button" onClick={closeDrawer}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors">
                {formSuccess ? 'Close' : 'Cancel'}
              </button>
              {!formSuccess && (
                <button
                  onClick={handleSubmit}
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formLoading ? 'Creating…' : 'Create Client'}
                </button>
              )}
              {formSuccess && createdClientId && (
                <button
                  onClick={() => { closeDrawer(); navigate(`/admin/users/${createdClientId}/detail`); }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500 transition-colors"
                >
                  Proceed to Client Profile
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={closeDeleteConfirm} />
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-1.5">
              Delete {selectedIds.size} client{selectedIds.size !== 1 ? 's' : ''}?
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              This permanently deletes the selected client profile{selectedIds.size !== 1 ? 's' : ''} and login account{selectedIds.size !== 1 ? 's' : ''}.
              This action cannot be undone. Clients with existing bookings or invoices cannot be deleted.
            </p>

            {deleteError && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">
                {deleteError}
              </div>
            )}

            {blockedClients.length === 0 ? (
              <>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Type <span className="font-semibold text-slate-800">confirm</span> to proceed
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="confirm"
                  autoFocus
                  className={inputCls(false)}
                />
              </>
            ) : !isSuperAdmin ? null : !overrideImpact ? (
              <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <p className="font-semibold mb-0.5">Blocked: {blockedClients.map(c => c.name).join(', ')}</p>
                <p>As Super Admin you can override this block and delete the client together with everything attached to it.</p>
              </div>
            ) : (
              <div>
                <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 mb-3">
                  <p className="font-semibold mb-1">
                    This permanently deletes {blockedClients.map(c => c.name).join(', ')} and:
                  </p>
                  {Object.keys(overrideImpact).length === 0 ? (
                    <p>No other records.</p>
                  ) : (
                    <ul className="max-h-40 overflow-y-auto space-y-0.5">
                      {Object.entries(overrideImpact)
                        .sort((a, b) => b[1] - a[1])
                        .map(([table, n]) => (
                          <li key={table} className="flex justify-between gap-3">
                            <span>{table.replace(/"/g, '').replace(/_/g, ' ')}</span>
                            <span className="tabular-nums font-semibold">{n}</span>
                          </li>
                        ))}
                    </ul>
                  )}
                  <p className="mt-1.5">This cannot be undone.</p>
                </div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Enter your password to confirm</label>
                <input
                  type="password"
                  value={overridePassword}
                  onChange={(e) => setOverridePassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleOverrideDelete(); }}
                  autoFocus
                  autoComplete="current-password"
                  className={inputCls(false)}
                />
              </div>
            )}

            <div className="flex items-center gap-2 mt-4">
              <button
                type="button"
                onClick={closeDeleteConfirm}
                disabled={deleteLoading || overrideLoading}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                {blockedClients.length > 0 ? 'Close' : 'Cancel'}
              </button>
              {blockedClients.length === 0 ? (
                <button
                  type="button"
                  onClick={handleDeleteConfirmed}
                  disabled={deleteLoading || deleteConfirmText.trim().toLowerCase() !== 'confirm'}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? 'Deleting…' : 'Delete'}
                </button>
              ) : isSuperAdmin && !overrideImpact ? (
                <button
                  type="button"
                  onClick={handleStartOverride}
                  disabled={overrideLoading}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {overrideLoading ? 'Checking…' : 'Override'}
                </button>
              ) : isSuperAdmin ? (
                <button
                  type="button"
                  onClick={handleOverrideDelete}
                  disabled={overrideLoading || !overridePassword}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {overrideLoading ? 'Deleting…' : 'Delete everything'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ClientManagement;
